import { Reply } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { styled, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppRoutes } from '~/app/app.routes';
import { Col, Text } from '../view.box';
import { Image } from '../view.image';
import { FeedbackDrawerItem, FeedbackDrawerTitle, FeedbackDrawerValue } from './feedback.drawer';
import { useFeedbackDrawerContext } from './feedback.drawer.ctx';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)({
	flexDirection: 'column',
	gap: 4,
});

const StyledToggleButton = styled(ToggleButton)({
	textTransform: 'none',
});

const Content: FC<{ active: boolean; primary: string; secondary?: string }> = ({ active, primary, secondary }) => (
	<Col flex={1} mx={2} alignItems="flex-start">
		<Text>{primary}</Text>
		{secondary && (
			<Text variant="caption" color="textSecondary">
				{secondary}
			</Text>
		)}
	</Col>
);

const Icons: FC<{ active: boolean; selected: boolean }> = ({ active, selected }) => {
	return (
		<Col height={'100%'} justifyContent={'flex-start'}>
			<Tooltip title={active ? 'Active' : 'Inactive'} placement="top">
				{active ? <CheckCircleOutlineIcon fontSize="small" /> : <NotInterestedIcon fontSize="small" />}
			</Tooltip>
			{selected && <Reply fontSize="small" color="primary" />}
		</Col>
	);
};

export const FeedbackDrawerSimilar = observer(() => {
	const {
		similarBrand,
		similarFragrance,
		useSimilarFragrances,
		handleChangeSimilarBrand,
		handleChangeSimilarFragrance,
	} = useFeedbackDrawerContext();

	const handleLinkClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
	}, []);

	const similar = useSimilarFragrances();

	if (!similar.fragrances?.length && !similar.brands?.length) {
		return (
			<FeedbackDrawerItem>
				<FeedbackDrawerTitle>Similar:</FeedbackDrawerTitle>
				<FeedbackDrawerValue>Not found</FeedbackDrawerValue>
			</FeedbackDrawerItem>
		);
	}

	return (
		<FeedbackDrawerItem style={{ flexDirection: 'column', gap: 8 }}>
			{!!similar.fragrances?.length && (
				<>
					<FeedbackDrawerTitle>Similar fragrances:</FeedbackDrawerTitle>
					<StyledToggleButtonGroup exclusive value={similarFragrance} onChange={handleChangeSimilarFragrance}>
						{similar.fragrances.map(item => (
							<StyledToggleButton key={item.id} value={item}>
								<Link
									target="_blank"
									onClick={handleLinkClick}
									to={AppRoutes.FragranceEdit.buildPath({ id: item.id })}
								>
									<Image src={item.image} />
								</Link>
								<Content active={item.active} primary={item.name} secondary={item.brandName} />
								<Icons active={item.active} selected={similarFragrance?.id === item.id} />
							</StyledToggleButton>
						))}
					</StyledToggleButtonGroup>
				</>
			)}
			{!!similar.brands?.length && (
				<>
					<FeedbackDrawerTitle>Similar brands:</FeedbackDrawerTitle>
					<StyledToggleButtonGroup exclusive value={similarBrand} onChange={handleChangeSimilarBrand}>
						{similar.brands.map(item => (
							<StyledToggleButton key={item.id} value={item}>
								<Link
									target="_blank"
									onClick={handleLinkClick}
									to={AppRoutes.BrandEdit.buildPath({ id: item.id })}
								>
									<Image src={item.image} />
								</Link>
								<Content active={item.active} primary={item.name} />
								<Icons active={item.active} selected={similarBrand?.id === item.id} />
							</StyledToggleButton>
						))}
					</StyledToggleButtonGroup>
				</>
			)}
		</FeedbackDrawerItem>
	);
});
